/**
 * 함께 쓰는 것 — 프로젝트와 라벨. **설정 화면의 카드 둘이다.**
 *
 * ⚠ **별도 페이지(`/projects`)였다가 2026-08-26 에 여기로 들어왔다**(지시).
 * 옛 판의 이유는 「설정은 내 것, 프로젝트는 모두의 것이라 섞지 않는다」였는데,
 * 그 가름 때문에 **이 도구의 단위가 프로젝트인 것처럼 보였다.** 단위는
 * 티켓이다 — 프로젝트는 티켓이 사는 자리를 정하는 설정이다.
 *
 * **프로젝트를 만드는 것은 `owner` 뿐이다.** 화면은 그 카드를 숨길 뿐이고
 * 막는 것은 서버다(`require_owner`).
 */

import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

import {
  createLabel,
  createProject,
  deleteLabel,
  deleteProject,
  errorMessage,
  listLabels,
  NotLoggedInError,
  updateLabel,
  updateProject,
  type Label,
  type LabelColor,
  type Project,
} from '../api';
import { LabelChip } from '../board/Chips';
import { Modal, ModalTitle } from '../ui/Modal';

export function ProjectsCard({
  projects,
  projectId,
  reloadProjects,
  onError,
  onUnauthorized,
}: {
  projects: Project[];
  projectId: number | null;
  reloadProjects: () => Promise<void>;
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
}) {
  const [making, setMaking] = useState(false);
  /** 고치는 중인 프로젝트. 지우기도 이 모달 안에 있다 — 되돌릴 수 없는
      단추를 목록에 늘어놓으면 언젠가 잘못 눌린다. */
  const [editing, setEditing] = useState<Project | null>(null);

  return (
    <Card>
      <CardContent className="pt-s5">
        <div className="mb-s4 flex items-start justify-between gap-s3">
          <ul className="m-0 flex list-none flex-col gap-s2 p-0">
            {projects.map((p) => (
              <li key={p.id} className="flex items-center gap-s3 text-meta">
                <span
                  className="tnum rounded-sm px-s2 py-[1px] text-micro font-bold"
                  style={{ background: 'var(--brand-t)', color: 'var(--brand-d)' }}
                >
                  {p.key}
                </span>
                <span className="text-ink">{p.name}</span>
                {p.id === projectId && (
                  <span className="text-micro text-ink-3">· 보고 있는 것</span>
                )}
                <button
                  type="button"
                  onClick={() => setEditing(p)}
                  aria-label={`${p.name} 고치기`}
                  className="grid size-s5 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-sunk hover:text-ink max-[720px]:size-s8"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path
                      d="M8.2 1.6l2.2 2.2M1.5 8.3l6.1-6.1 2.2 2.2-6.1 6.1-2.9.7z"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
          <Button size="sm" onClick={() => setMaking(true)}>
            새 프로젝트
          </Button>
        </div>

        {/* **만들기는 모달이다**(2026-08-26 지시). 늘 펴져 있는 폼은 목록보다
            자리를 먼저 먹는데, 프로젝트는 자주 만드는 것이 아니다. */}
        <Modal
          open={making}
          onClose={() => setMaking(false)}
          title="새 프로젝트"
          description="키는 티켓 번호 앞에 붙고 만든 뒤에는 못 바꿈."
          size="sm"
        >
          <NewProject
            reloadProjects={reloadProjects}
            onDone={() => setMaking(false)}
            onError={onError}
            onUnauthorized={onUnauthorized}
          />
        </Modal>

        <Modal
          open={editing !== null}
          onClose={() => setEditing(null)}
          title="프로젝트 고치기"
          description="키를 바꾸면 티켓 번호의 앞자리가 함께 바뀐다."
          size="sm"
        >
          {editing && (
            <EditProject
              project={editing}
              reloadProjects={reloadProjects}
              onDone={() => setEditing(null)}
              onError={onError}
              onUnauthorized={onUnauthorized}
            />
          )}
        </Modal>
      </CardContent>
    </Card>
  );
}

function NewProject({
  reloadProjects,
  onDone,
  onError,
  onUnauthorized,
}: {
  reloadProjects: () => Promise<void>;
  onDone: () => void;
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
}) {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !key.trim() || !name.trim()) return;
    setBusy(true);
    onError(null);
    try {
      const made = await createProject({ key: key.trim().toUpperCase(), name: name.trim() });
      // **목록을 다시 부른다.** 안 하면 헤더 선택기가 새로고침 전까지 옛
      // 목록을 쥔 채 남아 만든 사람 눈에는 아무 일도 안 일어난 것처럼 보인다.
      await reloadProjects();
      toast.success(`${made.key} 만들었다`);
      setKey('');
      setName('');
      onDone();
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`프로젝트를 만들지 못했다: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="flex flex-col gap-s4" onSubmit={(e) => void submit(e)}>
      <ModalTitle>새 프로젝트</ModalTitle>

      <label className="flex flex-col gap-s2">
        <span className="text-meta text-ink-3">키</span>
        {/* 키는 티켓 번호 앞에 붙는다 — `PLUS-118`. 그래서 짧고 대문자다. */}
        <Input
          autoFocus
          value={key}
          onChange={(e) => setKey(e.target.value.toUpperCase())}
          placeholder="PLUS"
          maxLength={10}
          className="tnum"
          aria-label="프로젝트 키"
        />
        <span className="text-small text-ink-3">
          예: <span className="tnum">PLUS-118</span>
        </span>
      </label>

      <label className="flex flex-col gap-s2">
        <span className="text-meta text-ink-3">이름</span>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="플러스미 오피스"
          maxLength={80}
          aria-label="프로젝트 이름"
        />
      </label>

      <div className="flex justify-end gap-s2">
        <Button type="button" variant="ghost" onClick={onDone}>
          취소
        </Button>
        <Button type="submit" disabled={busy || !key.trim() || !name.trim()}>
          {busy ? '만드는 중…' : '만들기'}
        </Button>
      </div>
    </form>
  );
}

/**
 * 프로젝트 고치기 — 이름·키, 그리고 지우기.
 *
 * **지우기를 목록이 아니라 이 안에 둔다.** 되돌릴 수 없는 단추를 줄마다
 * 늘어놓으면 언젠가 잘못 눌린다. 여기서는 한 번 더 눌러야 나온다.
 */
function EditProject({
  project,
  reloadProjects,
  onDone,
  onError,
  onUnauthorized,
}: {
  project: Project;
  reloadProjects: () => Promise<void>;
  onDone: () => void;
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
}) {
  const [key, setKey] = useState(project.key);
  const [name, setName] = useState(project.name);
  const [busy, setBusy] = useState(false);
  /** 지우기는 두 걸음이다. 첫 누름은 무엇이 사라지는지 말할 뿐이다. */
  const [confirming, setConfirming] = useState(false);

  const dirty = key !== project.key || name.trim() !== project.name;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !dirty || !key.trim() || !name.trim()) return;
    setBusy(true);
    onError(null);
    try {
      // **바뀐 것만 보낸다.** 안 바뀐 키를 함께 보내면 그 사이 남이 고친
      // 값을 옛 값으로 덮어쓴다.
      await updateProject(project.id, {
        ...(key !== project.key ? { key: key.trim().toUpperCase() } : {}),
        ...(name.trim() !== project.name ? { name: name.trim() } : {}),
      });
      await reloadProjects();
      toast.success('고쳤다');
      onDone();
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`프로젝트를 고치지 못했다: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    onError(null);
    try {
      await deleteProject(project.id);
      await reloadProjects();
      toast.success(`${project.key} 지웠다`);
      onDone();
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      // 티켓이 남아 있으면 서버가 몇 장인지 말해 준다 — 그대로 보여 준다.
      onError(`프로젝트를 지우지 못했다: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="flex flex-col gap-s4" onSubmit={(e) => void submit(e)}>
      <ModalTitle>프로젝트 고치기</ModalTitle>

      <label className="flex flex-col gap-s2">
        <span className="text-meta text-ink-3">키</span>
        <Input
          autoFocus
          value={key}
          onChange={(e) => setKey(e.target.value.toUpperCase())}
          maxLength={10}
          className="tnum"
          aria-label="프로젝트 키"
        />
        <span className="text-small text-ink-3">
          티켓 번호 앞에 붙는다 — <span className="tnum">{key || 'PLUS'}-118</span>.
          바꾸면 밖에 적어 둔 번호와 어긋난다.
        </span>
      </label>

      <label className="flex flex-col gap-s2">
        <span className="text-meta text-ink-3">이름</span>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          aria-label="프로젝트 이름"
        />
      </label>

      <div className="flex flex-wrap items-center justify-between gap-s3 border-t border-line pt-s4">
        {confirming ? (
          <div className="flex flex-wrap items-center gap-s3">
            <span className="text-meta text-st-red-f">
              지우면 되돌릴 수 없다. 티켓이 있으면 안 지워진다.
            </span>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => void remove()}
            >
              {busy ? '지우는 중…' : '정말 지운다'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
              그만
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="ghost"
            className="text-ink-3"
            onClick={() => setConfirming(true)}
          >
            이 프로젝트 지우기
          </Button>
        )}

        <div className="ml-auto flex gap-s2">
          <Button type="button" variant="ghost" onClick={onDone}>
            취소
          </Button>
          <Button type="submit" disabled={busy || !dirty || !key.trim() || !name.trim()}>
            {busy ? '저장 중…' : '저장'}
          </Button>
        </div>
      </div>
    </form>
  );
}

/** 고른 색. 라벨은 프로젝트마다 따로 산다. */
const COLOR_OPTIONS: LabelColor[] = ['gray', 'blue', 'amber', 'green', 'red', 'violet'];

export function LabelsCard({
  project,
  onError,
  onUnauthorized,
}: {
  project: Project;
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
}) {
  const [labels, setLabels] = useState<Label[]>([]);
  const [name, setName] = useState('');
  const [color, setColor] = useState<LabelColor>('blue');
  const [busy, setBusy] = useState(false);
  /** 고치는 중인 라벨. **지우기와 다른 길이다** — 지우면 붙어 있던 티켓에서
      함께 빠지고, 고치면 id 가 그대로라 아무것도 안 떨어진다. */
  const [editing, setEditing] = useState<Label | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await listLabels(project.id);
        if (!cancelled) setLabels(data.labels);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotLoggedInError) {
          onUnauthorized();
          return;
        }
        onError(`라벨을 불러오지 못했다: ${errorMessage(err)}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project.id, onError, onUnauthorized]);

  async function add(event: FormEvent) {
    event.preventDefault();
    if (busy || !name.trim()) return;
    setBusy(true);
    onError(null);
    try {
      const made = await createLabel({
        project_id: project.id,
        name: name.trim(),
        color,
      });
      setLabels((prev) => [...prev, made].sort((a, b) => a.name.localeCompare(b.name, 'ko')));
      toast.success(`「${made.name}」 만들었다`);
      setName('');
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`라벨을 만들지 못했다: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove(label: Label) {
    onError(null);
    try {
      await deleteLabel(label.id);
      setLabels((prev) => prev.filter((l) => l.id !== label.id));
      // **붙어 있던 티켓에서도 함께 빠진다**(`ticket_labels` 가 CASCADE 다).
      // 그것을 말해 주지 않으면 「라벨만 지웠는데 티켓이 왜 바뀌었나」가 된다.
      toast.success(`「${label.name}」 지웠다 — 붙어 있던 티켓에서도 빠졌다`);
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`라벨을 지우지 못했다: ${errorMessage(err)}`);
    }
  }

  return (
    <Card>
      <CardContent className="pt-s5">
        {/* **소제목이 아니라 이름표다.** 카드 제목을 걷으면서 「이게 어느
            프로젝트의 라벨인가」가 사라졌다 — 아래 「새 라벨」·「색」과 같은
            무게로 왼쪽에 세워 그 정보만 되살린다. */}
        <div className="mb-s4 flex flex-wrap items-center gap-x-s4 gap-y-s2">
          <span className="text-meta text-ink-3">{project.name} 라벨</span>
          {labels.length === 0 ? (
          <p className="m-0 text-meta text-ink-3">라벨 없음</p>
        ) : (
          <ul className="m-0 flex list-none flex-wrap gap-s2 p-0">
            {labels.map((label) => (
              <li key={label.id} className="flex items-center gap-s2">
                {/* **칩 자체가 고치기 단추다.** 줄마다 연필을 하나 더 두면
                    라벨 하나에 단추가 둘이 되어 목록이 시끄러워진다. */}
                <button
                  type="button"
                  onClick={() => setEditing(label)}
                  aria-label={`${label.name} 라벨 고치기`}
                  className="rounded-sm focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <LabelChip label={label} />
                </button>
                <button
                  type="button"
                  onClick={() => void remove(label)}
                  aria-label={`${label.name} 라벨 지우기`}
                  className="grid size-s5 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-sunk hover:text-ink max-[720px]:size-s8"
                >
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                    <path
                      d="M2.5 2.5l6 6M8.5 2.5l-6 6"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
        </div>

        <form
          className="flex flex-wrap items-end gap-s3 border-t border-line pt-s4"
          onSubmit={(e) => void add(e)}
        >
          <label className="flex min-w-[160px] flex-1 flex-col gap-s2">
            <span className="text-meta text-ink-3">새 라벨</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="긴급"
              maxLength={30}
              aria-label="라벨 이름"
            />
          </label>
          {/*
            색은 **여섯뿐이다** — `styles.css` 의 상태색 토큰이 그만큼이고,
            늘리려면 테마 일곱 곳을 함께 고쳐야 한다. 미리보기로 고르게 해서
            이름만 보고 상상하지 않게 한다.
          */}
          <div className="flex flex-col gap-s2">
            <span className="text-meta text-ink-3">색</span>
            <div role="radiogroup" aria-label="라벨 색" className="flex gap-s2">
              {COLOR_OPTIONS.map((c) => (
                <label key={c} className="cursor-pointer">
                  <input
                    type="radio"
                    name="label-color"
                    checked={color === c}
                    onChange={() => setColor(c)}
                    aria-label={c}
                    className="peer sr-only"
                  />
                  <span
                    className="block size-s6 rounded-sm border-2 border-transparent peer-checked:border-brand peer-focus-visible:ring-2 peer-focus-visible:ring-brand"
                    style={{ background: `var(--st-${c}-f)` }}
                  />
                </label>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={busy || !name.trim()}>
            {busy ? '만드는 중…' : '더하기'}
          </Button>
        </form>

        <Modal
          open={editing !== null}
          onClose={() => setEditing(null)}
          title="라벨 고치기"
          description="붙어 있던 티켓에서 안 떨어진다."
          size="sm"
        >
          {editing && (
            <EditLabel
              label={editing}
              onSaved={(next) =>
                setLabels((prev) =>
                  prev
                    .map((l) => (l.id === next.id ? next : l))
                    .sort((a, b) => a.name.localeCompare(b.name, 'ko')),
                )
              }
              onDone={() => setEditing(null)}
              onError={onError}
              onUnauthorized={onUnauthorized}
            />
          )}
        </Modal>
      </CardContent>
    </Card>
  );
}


/**
 * 라벨 고치기 — 이름과 색.
 *
 * **지웠다 다시 만드는 것과 다르다.** 지우면 `ticket_labels` 가 CASCADE 로
 * 함께 빠져 붙어 있던 티켓에서 라벨이 다 떨어진다. 여기서는 id 가 그대로다.
 */
function EditLabel({
  label,
  onSaved,
  onDone,
  onError,
  onUnauthorized,
}: {
  label: Label;
  onSaved: (next: Label) => void;
  onDone: () => void;
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
}) {
  const [name, setName] = useState(label.name);
  const [color, setColor] = useState<LabelColor>(label.color);
  const [busy, setBusy] = useState(false);

  const dirty = name.trim() !== label.name || color !== label.color;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !dirty || !name.trim()) return;
    setBusy(true);
    onError(null);
    try {
      const next = await updateLabel(label.id, {
        ...(name.trim() !== label.name ? { name: name.trim() } : {}),
        ...(color !== label.color ? { color } : {}),
      });
      onSaved(next);
      toast.success('고쳤다');
      onDone();
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`라벨을 고치지 못했다: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="flex flex-col gap-s4" onSubmit={(e) => void submit(e)}>
      <ModalTitle>라벨 고치기</ModalTitle>

      <label className="flex flex-col gap-s2">
        <span className="text-meta text-ink-3">이름</span>
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={30}
          aria-label="라벨 이름"
        />
      </label>

      <div className="flex flex-col gap-s2">
        <span className="text-meta text-ink-3">색</span>
        <div role="radiogroup" aria-label="라벨 색" className="flex gap-s2">
          {COLOR_OPTIONS.map((c) => (
            <label key={c} className="cursor-pointer">
              <input
                type="radio"
                name="edit-label-color"
                checked={color === c}
                onChange={() => setColor(c)}
                aria-label={c}
                className="peer sr-only"
              />
              <span
                className="block size-s6 rounded-sm border-2 border-transparent peer-checked:border-brand peer-focus-visible:ring-2 peer-focus-visible:ring-brand"
                style={{ background: `var(--st-${c}-f)` }}
              />
            </label>
          ))}
        </div>
      </div>

      {/* 지금 어떻게 보이는지 그대로 보여 준다 — 이름만 보고 상상하지 않게. */}
      <div className="flex items-center gap-s3 border-t border-line pt-s4">
        <span className="text-meta text-ink-3">이렇게 보임</span>
        <LabelChip label={{ ...label, name: name.trim() || label.name, color }} />
      </div>

      <div className="flex justify-end gap-s2">
        <Button type="button" variant="ghost" onClick={onDone}>
          취소
        </Button>
        <Button type="submit" disabled={busy || !dirty || !name.trim()}>
          {busy ? '저장 중…' : '저장'}
        </Button>
      </div>
    </form>
  );
}
